import type {
  CodexCapacitySeries,
  PublicStatusBucketRecord,
  PublicStatusNamespaceRecord,
  PublicStatusState,
  TenantCodexCapacityResponse,
} from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { localizeCaughtError } from '../api/errorMessages.ts';
import { useCodexCapacity, useNxtpathPublicStatus, usePlatformHealth } from '../api/useStatus.ts';
import { formatTimestamp } from '../components/formatters.ts';
import { STATE_TONE, stateText } from '../components/publicStatus.ts';
import { useLocale, useT } from '../i18n/index.ts';
import {
  Button,
  EmptyState,
  KpiCard,
  PageHeader,
  Panel,
  Skeleton,
  StatusLamp,
  TableSkeleton,
  cx,
} from '../ui/index.ts';
import {
  capacityBySku,
  capacityTier,
  foldCapacityToBuckets,
  hasCapacitySample,
  worseState,
} from './status/capacityOverlay.ts';

/**
 * `/status` — the service-status surface as a full page.
 *
 * The overall-state KPI lamp is the ONE pulsing element on the page
 * (DESIGN.md §2). Availability refresh is `useNxtpathPublicStatus`'s
 * staleTime. Codex at-capacity is a second query: a non-null ratio paints
 * on the codex namespace row and matching model rows (15% / 30%) ahead of
 * availability. A null ratio renders nothing. A missing or failed capacity
 * response changes nothing.
 *
 * Layout: each namespace renders as a timeline row (48 × 30-min blocks
 * over 24h) with member-SKU mini timelines behind a disclosure, and
 * "recent events" derive from the namespace history transitions
 * (traffic buckets only).
 *
 * Namespaces are the whole vocabulary of this page. The payload also
 * carries a legacy vendor/channel rollup; this console does NOT render
 * it, and a payload without `namespaces[]` gets an empty state instead
 * of a downgraded channel-era view.
 *
 * That is the namespace-only cut every other status surface already
 * took, not an upstream-identity redaction: `apps/landing-web` moved to
 * `namespaces[]` with a graceful empty state in epic #898 P6 (#918),
 * and console-web's `/access/status` dropped its vendor fallback in
 * #916. `PublicStatusResponse` is channel-facing by construction and
 * "never leaks upstream identity" (`packages/contracts/src/public-status.ts`);
 * upstream identity is a different type family entirely
 * (`internal-upstreams.ts`), so rendering vendors would not have
 * crossed the tenant/upstream boundary — it would only have shown a
 * vocabulary this page no longer speaks.
 */

const TIMELINE_BLOCK: Record<PublicStatusState, string> = {
  healthy: 'bg-ok',
  busy: 'bg-warn',
  unavailable: 'bg-bad',
};

function formatBucketTime(iso: string, locale: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) {
    return '—';
  }
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(ms));
}

/**
 * State transitions reconstructed from the namespace timelines. Only
 * buckets that carried traffic participate — a zero-request bucket can't
 * witness a recovery, so it never emits an event. (Since #113 the
 * timeline PAINTS idle buckets as healthy, but that is a display-only
 * decision; letting them through here would fabricate transitions out
 * of silence.)
 */
interface DerivedEvent {
  readonly key: string;
  readonly namespaceDisplayName: string;
  readonly from: PublicStatusState;
  readonly to: PublicStatusState;
  readonly at: string;
}

function deriveNamespaceEvents(namespaces: readonly PublicStatusNamespaceRecord[]): DerivedEvent[] {
  const events: DerivedEvent[] = [];
  for (const namespace of namespaces) {
    let previous: PublicStatusState | null = null;
    for (const bucket of namespace.history ?? []) {
      if (bucket.request_count === 0) continue;
      if (previous !== null && bucket.state !== previous) {
        events.push({
          key: `${namespace.namespace_id}:${bucket.bucket_start}`,
          namespaceDisplayName: namespace.namespace_display_name,
          from: previous,
          to: bucket.state,
          at: bucket.bucket_start,
        });
      }
      previous = bucket.state;
    }
  }
  return events.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 8);
}

export function Status(): JSX.Element {
  const t = useT();
  const statusQ = useNxtpathPublicStatus();
  const healthQ = usePlatformHealth();
  const capacityQ = useCodexCapacity();
  const status = statusQ.data;
  const namespaces = status?.namespaces ?? [];
  const hasNamespaces = namespaces.length > 0;

  return (
    <section data-testid="status-page" className="flex flex-col gap-4">
      <PageHeader title={t('status.title')} sub={t('status.subtitle')} testId="status-header" />

      {statusQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="status-loading">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : statusQ.isError ? (
        <div role="alert" data-testid="status-error" className="flex flex-wrap items-center gap-3">
          <p className="m-0 text-body text-bad">
            {localizeCaughtError(statusQ.error, t, 'status.error')}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void statusQ.refetch();
            }}
            data-testid="status-retry"
          >
            {t('status.retry')}
          </Button>
        </div>
      ) : status !== undefined ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard
              testId="status-kpi-overall"
              label={t('status.kpi.overall')}
              lamp={STATE_TONE[status.summary.state]}
              pulse
              tone={
                status.summary.state === 'healthy'
                  ? 'ok'
                  : status.summary.state === 'busy'
                    ? 'warn'
                    : 'bad'
              }
              value={stateText(status.summary.state, t)}
              // The hint counts namespaces, while the main value comes from
              // the channel-grain `summary`. With no namespaces there is
              // nothing to count, and "0/0 services healthy" under a
              // "Healthy" lamp reads as a contradiction — so the hint is
              // simply absent there, same as any other unknown metric.
              delta={
                hasNamespaces
                  ? {
                      text: t('status.kpi.overall_hint', {
                        healthy: namespaces.filter(
                          (namespace) => (namespace.state ?? 'healthy') === 'healthy',
                        ).length,
                        total: namespaces.length,
                      }),
                      tone: 'neutral',
                    }
                  : undefined
              }
            />
            <HealthKpi healthQ={healthQ} />
            <KpiCard
              testId="status-kpi-updated"
              label={t('status.kpi.updated')}
              value={status.updated_at === null ? '—' : formatTimestamp(status.updated_at)}
              delta={{ text: t('status.kpi.updated_hint'), tone: 'neutral' }}
            />
          </div>

          {hasNamespaces ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
              <NamespaceTimelinePanel
                namespaces={namespaces}
                windowHours={status.window_hours}
                overlay={capacityQ.data}
              />
              <DerivedEventsPanel namespaces={namespaces} />
            </div>
          ) : (
            <Panel caption={t('status.timeline.caption')} testId="status-namespaces-empty">
              <EmptyState
                title={t('status.empty.title')}
                hint={t('status.empty.hint')}
                testId="status-empty"
              />
            </Panel>
          )}
        </>
      ) : (
        <TableSkeleton rows={3} testId="status-pending" />
      )}
    </section>
  );
}

function formatAvailability(pct: number): string {
  return `${pct.toFixed(2)}%`;
}

function formatCapacityRatio(ratio: number): string {
  return ratio.toFixed(1);
}

function UptimeTimeline({
  history,
  dense = false,
  testId,
  capacity,
}: {
  readonly history: readonly PublicStatusBucketRecord[];
  readonly dense?: boolean;
  readonly testId?: string;
  /** Per-bucket at-capacity ratio, aligned with `history`. Omit to paint availability only. */
  readonly capacity?: readonly (number | null)[];
}): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  return (
    <div className={cx('flex items-stretch gap-px', dense ? 'h-3' : 'h-6')} data-testid={testId}>
      {history.map((bucket, index) => {
        const noData = bucket.request_count === 0;
        const start = formatBucketTime(bucket.bucket_start, locale);
        const end = formatBucketTime(bucket.bucket_end, locale);
        const availabilityTitle = noData
          ? t('status.timeline.block_no_data', { start, end })
          : t('status.timeline.block_title', {
              start,
              end,
              rate: bucket.success_rate_pct.toFixed(2),
            });
        const sample = capacity === undefined ? undefined : (capacity[index] ?? null);
        const tier = sample === undefined || sample === null ? null : capacityTier(sample);
        const title =
          sample === undefined || sample === null
            ? availabilityTitle
            : `${availabilityTitle} · ${t('status.capacity.bucket_suffix', {
                ratio: formatCapacityRatio(sample),
              })}`;
        const paint = tier ?? (noData ? 'healthy' : bucket.state);
        return (
          <div
            key={bucket.bucket_start}
            data-state={noData ? 'none' : bucket.state}
            data-capacity-tier={sample === undefined ? undefined : tier === null ? 'none' : tier}
            title={title}
            className={cx(
              'min-w-0 flex-1 rounded-sm',
              // #113: idle buckets read as healthy — a quiet service is a
              // fine service, and the neutral tint made zero-traffic rows
              // look unmonitored. data-state keeps the none/healthy
              // distinction for tests and tooling; only the paint merges.
              // A capacity sample, when present, replaces that paint.
              TIMELINE_BLOCK[paint],
            )}
          />
        );
      })}
    </div>
  );
}

function NamespaceTimelineRow({
  namespace,
  windowHours,
  namespaceSeries,
  skuSeries,
}: {
  readonly namespace: PublicStatusNamespaceRecord;
  readonly windowHours: number;
  /** Present only for the codex namespace, and only when the capacity feed loaded. */
  readonly namespaceSeries?: CodexCapacitySeries;
  readonly skuSeries?: ReadonlyMap<string, CodexCapacitySeries>;
}): JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const availabilityState = namespace.state ?? 'healthy';
  const state =
    namespaceSeries === undefined
      ? availabilityState
      : worseState(availabilityState, capacityTier(namespaceSeries.ratio_24h));
  const skuCount = namespace.skus.length;
  const namespaceCapacity =
    namespaceSeries !== undefined && namespace.history !== undefined
      ? foldCapacityToBuckets(namespaceSeries, namespace.history)
      : undefined;

  return (
    <div
      className="flex flex-col gap-2 border-b border-line-soft px-4 py-3 last:border-b-0"
      data-testid={`status-namespace-row-${namespace.namespace_slug}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <StatusLamp tone={STATE_TONE[state]} size="sm" className="self-center" />
        <span className="text-body font-medium text-ink">{namespace.namespace_display_name}</span>
        <span className="font-mono text-caption text-ink-low">{namespace.namespace_slug}</span>
        {/*
         * The lamp is `aria-hidden` decoration and its tone is the only
         * COLOUR the row spends on state, so the state is spelled out
         * here (WCAG 1.4.1). Visible rather than `sr-only`: a reader who
         * cannot tell the tones apart is not necessarily using assistive
         * tech. Same three words the dashboard strip puts in its chips —
         * one `stateText`, one vocabulary (`components/publicStatus.ts`).
         */}
        <span
          className="text-caption text-ink-mid"
          data-testid={`status-namespace-state-${namespace.namespace_slug}`}
        >
          {stateText(state, t)}
        </span>
        <span
          className="ml-auto font-mono text-body font-medium tabular-nums text-ink"
          data-testid={`status-namespace-availability-${namespace.namespace_slug}`}
        >
          {formatAvailability(namespace.availability_pct)}
        </span>
        <span className="text-caption text-ink-low">{t('status.timeline.uptime')}</span>
      </div>

      {namespace.history !== undefined && namespace.history.length > 0 ? (
        <>
          <UptimeTimeline
            history={namespace.history}
            capacity={namespaceCapacity}
            testId={`status-namespace-timeline-${namespace.namespace_slug}`}
          />
          <div className="flex justify-between font-mono text-caption text-ink-low">
            <span>{t('status.timeline.window_start', { hours: windowHours })}</span>
            <span>{t('status.timeline.window_now')}</span>
          </div>
        </>
      ) : null}

      {skuCount > 0 ? (
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((open) => !open);
            }}
            data-testid={`status-namespace-skus-toggle-${namespace.namespace_slug}`}
          >
            {expanded
              ? t('status.timeline.skus_hide')
              : t('status.timeline.skus_show', { count: skuCount })}
          </Button>
          {expanded ? (
            <ul className="m-0 flex list-none flex-col gap-3 p-0 pl-4 max-md:pl-0">
              {namespace.skus.map((sku) => {
                const matched = skuSeries?.get(sku.sku_name);
                const skuCapacity =
                  matched !== undefined && sku.history !== undefined
                    ? foldCapacityToBuckets(matched, sku.history)
                    : undefined;
                return (
                  <li key={sku.sku_id} data-testid={`status-sku-row-${sku.sku_id}`}>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="break-all font-mono text-body text-ink-mid">
                        {sku.sku_name}
                      </span>
                      <span className="ml-auto font-mono text-body tabular-nums text-ink-mid">
                        {formatAvailability(sku.availability_pct)}
                      </span>
                    </div>
                    {sku.history !== undefined && sku.history.length > 0 ? (
                      <UptimeTimeline
                        history={sku.history}
                        dense
                        capacity={skuCapacity}
                        testId={`status-sku-timeline-${sku.sku_id}`}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NamespaceTimelinePanel({
  namespaces,
  windowHours,
  overlay,
}: {
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
  readonly windowHours: number;
  /** Set only when the capacity query has data. Absent means the page is unchanged. */
  readonly overlay?: TenantCodexCapacityResponse;
}): JSX.Element {
  const t = useT();
  const bySku = overlay === undefined ? undefined : capacityBySku(overlay);
  const showCapacityLegend = overlay !== undefined && hasCapacitySample(overlay);
  return (
    <Panel
      caption={
        showCapacityLegend ? (
          <span className="inline-flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span>{t('status.timeline.caption')}</span>
            <span className="text-caption text-ink-low" data-testid="status-capacity-legend">
              {t('status.capacity.legend')}
            </span>
          </span>
        ) : (
          t('status.timeline.caption')
        )
      }
      actions={
        <span className="text-caption text-ink-low">
          {t('status.timeline.window', { hours: windowHours })}
        </span>
      }
      testId="status-namespaces"
    >
      {namespaces.map((namespace) => (
        <NamespaceTimelineRow
          key={namespace.namespace_id}
          namespace={namespace}
          windowHours={windowHours}
          namespaceSeries={
            overlay !== undefined && namespace.namespace_slug === 'codex'
              ? overlay.overall
              : undefined
          }
          skuSeries={bySku}
        />
      ))}
    </Panel>
  );
}

function DerivedEventsPanel({
  namespaces,
}: {
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
}): JSX.Element {
  const t = useT();
  const events = deriveNamespaceEvents(namespaces);
  return (
    <Panel caption={t('status.events.caption')} padded testId="status-events">
      {events.length === 0 ? (
        <p className="m-0 text-body text-ink-low" data-testid="status-events-empty">
          {t('status.events.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {events.map((event) => (
            <li
              key={event.key}
              data-testid={`status-event-${event.key}`}
              className="rounded border border-line-soft bg-well px-3 py-2"
            >
              <p className="m-0 truncate text-body text-ink">{event.namespaceDisplayName}</p>
              <p className="m-0 mt-1 font-mono text-caption text-ink-low">
                {stateText(event.from, t)} → {stateText(event.to, t)} · {formatTimestamp(event.at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function HealthKpi({
  healthQ,
}: {
  readonly healthQ: UseQueryResult<{ readonly ok: true }>;
}): JSX.Element {
  const t = useT();
  const healthy = healthQ.data?.ok === true;
  return (
    <KpiCard
      testId="status-kpi-health"
      label={t('status.kpi.health')}
      tone={healthy ? 'ok' : healthQ.isError ? 'bad' : 'default'}
      value={healthy ? t('status.health.ok') : healthQ.isError ? t('status.health.down') : '—'}
      delta={{ text: t('status.kpi.health_hint'), tone: 'neutral' }}
    />
  );
}
